import type { NativeReportColumn } from '@/api/reports';
import type { ReportDimension, ReportMetadata } from '@/types/reports';

import { GRANULARITY_SUFFIXES } from '../constants';
import { isAggregationColumn } from './nativeDataTransforms';

/**
 * Convert a native-query column key to a dot-format Cube dimension name.
 *
 * The native query engine emits time-granularity columns with a double-
 * underscore separator (e.g. `ahoy_close_date__week`), but the date-detection
 * helpers in `constants.ts` (`isDateDimensionWithGranularity`,
 * `extractGranularityFromDimension`) expect the Cube dot format
 * (`CrmObject.ahoy_close_date.week`). This function performs that conversion
 * so the chronological-sort logic fires correctly for all consumers of
 * ChartRendererCore.
 */
export function deriveDimensionNamesFromColumns(
  columns: NativeReportColumn[]
): string[] {
  return columns
    .filter(col => !isAggregationColumn(col))
    .map(col => {
      let { key } = col;
      for (const suffix of GRANULARITY_SUFFIXES) {
        if (key.endsWith(`__${suffix}`)) {
          key = `${key.slice(0, -(suffix.length + 2))}.${suffix}`;
          break;
        }
      }
      return `CrmObject.${key}`;
    });
}

export interface GroupByForDimension {
  property?: string;
  timeGranularity?: string;
}

/**
 * The property name a native query understands for a dimension.
 *
 * Report metadata names custom dimensions `custom_<property>` and builtins
 * with their `ahoy_` prefix intact, but the native query API validates
 * `group_by` / `aggregations[].property` against the real
 * `property_type_name`. Cube-native and association dimensions carry no
 * property type, so their metadata name is the query name.
 */
export function nativePropertyName(dimension: ReportDimension): string {
  return dimension.property_type_name ?? dimension.name;
}

/**
 * Find the metadata dimension for a property name, whichever naming the
 * caller holds — the metadata dimension name (what the picker emits) or the
 * native property name (what a saved query stores).
 */
export function findDimensionForProperty(
  dimensions: ReportDimension[],
  propertyName: string
): ReportDimension | undefined {
  return dimensions.find(
    d => d.name === propertyName || d.property_type_name === propertyName
  );
}

/** Whether a dimension is in a selection held under either naming. */
export function isDimensionSelected(
  dimension: ReportDimension,
  selectedProperties: string[]
): boolean {
  return (
    selectedProperties.includes(dimension.name) ||
    (dimension.property_type_name !== null &&
      selectedProperties.includes(dimension.property_type_name))
  );
}

/**
 * Resolve the Cube dimension identifiers for a report's group-by fields,
 * matching what the wizard preview passes to ChartRenderer. Order is
 * preserved so values align positionally with chart xValues.
 */
export function deriveChartDimensionNames(
  groupBy: GroupByForDimension[],
  backendMeta: ReportMetadata
): string[] {
  return groupBy
    .map(gb => {
      if (!gb.property) {
        return undefined;
      }
      for (const objectType in backendMeta) {
        const dim = findDimensionForProperty(
          backendMeta[objectType].all,
          gb.property
        );
        if (dim && dim.cube_field) {
          if (dim.cube_type === 'time' && gb.timeGranularity) {
            return `${dim.cube_field}.${gb.timeGranularity}`;
          }
          return dim.cube_field;
        }
      }
      return undefined;
    })
    .filter((name): name is string => name !== undefined);
}
